import React from 'react'
import styled from 'styled-components'
import { images } from 'images'

/**
 * Header for the Urban Sports Club voucher flow: the BodyFast × USC lockup.
 *
 * Always co-branded — this flow only ever serves USC, so there is no switch. The
 * lockup needs USC's sign-off before the URL ships (§2.4).
 *
 * Lockup follows the USC Brand Guide (brand.urbansportsclub.com → Logo → Partnerships):
 *  - partner logo paired with the USC logotype, optically balanced in size
 *  - clearspace between the logos = half the width of the Unicon (the "U" shield)
 *  - the two are separated by a cross sized like the Arrow of the Unicon
 *  - the USC logotype is only ever used in black or white, never recoloured,
 *    distorted, rotated, cropped, or given effects
 */

const BODYFAST_LOGO_HEIGHT_PX = 30
// The USC landscape lockup carries the Unicon plus the wordmark, so matching
// BodyFast's height exactly makes its type read smaller — sized down slightly for
// the optical balance the Brand Guide asks for.
const USC_LOGO_HEIGHT_PX = 26
// The Unicon spans the lockup's full height and is roughly square, so its width
// tracks the rendered height. Brand Guide: clearspace between paired logos is half
// a Unicon.
const LOCKUP_GAP_PX = USC_LOGO_HEIGHT_PX / 2
// Brand Guide: the separating cross matches the Arrow, ~a third of the Unicon.
const CROSS_SIZE_PX = Math.round(USC_LOGO_HEIGHT_PX / 3)

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${LOCKUP_GAP_PX}px;
  padding: ${({ theme }) => theme.spacing.large} 0;
`

const BodyFastLogo = styled.img`
  height: ${BODYFAST_LOGO_HEIGHT_PX}px;
  width: auto;
`

const UscLogo = styled.img`
  height: ${USC_LOGO_HEIGHT_PX}px;
  width: auto;
`

/**
 * Drawn rather than imported so it inherits the header's colour and always
 * matches the Arrow size the Brand Guide asks for.
 */
const Cross = styled.span`
  position: relative;
  flex: 0 0 auto;
  width: ${CROSS_SIZE_PX}px;
  height: ${CROSS_SIZE_PX}px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.text};
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`

const UscHeader: React.FC = () => (
  <Wrapper data-testid='usc-header-cobranded'>
    <BodyFastLogo src={images.LogoWebp} alt='BodyFast' />
    <Cross aria-hidden='true' />
    <UscLogo src={images.uscLogoBlack} alt='Urban Sports Club' />
  </Wrapper>
)

export default UscHeader
